/* eslint-disable react/no-array-index-key */
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  IconButton,
  Input,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { darken, transparentize } from 'polished';
import { ReactComponent as CircleCheckIcon } from 'src/assets/icons/linkage-circle-checked.svg';
import styled, { css } from 'styled-components';

import { ReactComponent as ArrowDownIcon } from '@/assets/icons/arrow-down.svg';
import { ReactComponent as LinkingIcon } from '@/assets/icons/linking.svg';
import { LINKAGE_VISIBLE_COLUMNS } from '@/features/canvas/constants';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { LinkageColumn, LinkageRow, LinkedColumn } from '@/features/canvas/types/linkage';
import { getRoadmapCurrency } from '@/features/canvas/utils/csv-currency';
import { validateCell } from '@/features/canvas/utils/csvValidation';

import { LinkingTableFooter } from './LinkingTableFooter';

interface LinkingTableProps {
  dataColumns: LinkageColumn[];
  dataRows: LinkageRow[];
  editedValues: Record<string, Record<string, string>>;
  linkedColumns: LinkedColumn[];
  onSubmit: (editedValues: Record<string, Record<string, string>>) => void;
  setEditedValues: Dispatch<SetStateAction<Record<string, Record<string, string>>>>;
  setIsFieldLinkingDialogOpen: Dispatch<SetStateAction<boolean>>;
  setLinkedColumns: Dispatch<SetStateAction<LinkedColumn[]>>;
  setLinkingColumnId: Dispatch<SetStateAction<string | null>>;
  onSaveTemplate?: (name: string) => void;
}

const LinkingTable = ({
  linkedColumns,
  setLinkedColumns,
  setIsFieldLinkingDialogOpen,
  setLinkingColumnId,
  dataRows,
  dataColumns,
  onSubmit,
  onSaveTemplate,
  editedValues,
  setEditedValues,
}: LinkingTableProps) => {
  const { initiativeBudgets } = useEditorContext();
  const roadmapCurrency = useMemo(() => getRoadmapCurrency(initiativeBudgets), [initiativeBudgets]);

  // Removed pagination - showing all rows with scroll

  const [columnStartIndex, setColumnStartIndex] = useState(0);

  // Cell editing state
  const [editingCell, setEditingCell] = useState<{ columnId: string; rowId: string } | null>(null);

  const visibleColumns = useMemo(() => {
    return dataColumns.slice(columnStartIndex, columnStartIndex + LINKAGE_VISIBLE_COLUMNS);
  }, [columnStartIndex, dataColumns]);

  // Show all rows without pagination
  const visibleRows = dataRows;

  // Get cell value (edited value if exists, otherwise original)
  const getCellValue = (rowId: string, columnId: string, originalValue: string) => {
    return editedValues[rowId]?.[columnId] ?? originalValue;
  };

  // Handle cell editing
  const handleCellClick = (rowId: string, columnId: string) => {
    setEditingCell({ rowId, columnId });
  };

  const handleCellSave = (rowId: string, columnId: string, newValue: string) => {
    setEditedValues(prev => ({
      ...prev,
      [rowId]: { ...prev[rowId], [columnId]: newValue },
    }));
    setEditingCell(null);
  };

  const handleCellCancel = () => {
    setEditingCell(null);
  };

  // Calculate validation for each cell
  const getCellValidation = (columnId: string, cellValue: string) => {
    const linkedColumn = linkedColumns.find(lk => lk.importedColumnUuid === columnId);
    if (!linkedColumn) {
      return null; // Not linked, no validation
    }
    return validateCell(cellValue, linkedColumn.linkedTargetType, roadmapCurrency);
  };

  // Check if a column has any validation errors
  const columnHasErrors = (columnId: string) => {
    const linkedColumn = linkedColumns.find(lk => lk.importedColumnUuid === columnId);
    if (!linkedColumn) {
      return false;
    }

    const columnIndex = dataColumns.findIndex(col => col.id === columnId);
    if (columnIndex === -1) return false;

    return dataRows.some(row => {
      const originalValue = row.cells[columnIndex]?.value || '';
      const cellValue = getCellValue(row.id, columnId, originalValue);
      const validation = validateCell(cellValue, linkedColumn.linkedTargetType, roadmapCurrency);
      return !validation.isValid;
    });
  };

  const openFieldLinkingDialog = (columnId: string) => {
    setIsFieldLinkingDialogOpen(true);
    setLinkingColumnId(columnId);
  };

  const handleColumnHeaderClick = (columnId: string) => {
    const isColumnLinked = linkedColumns.some(lk => lk.importedColumnUuid === columnId);
    if (isColumnLinked) {
      const newLinkedColumns = linkedColumns.filter(lk => lk.importedColumnUuid !== columnId);
      setLinkedColumns(newLinkedColumns);
      return;
    }
    openFieldLinkingDialog(columnId);
  };

  const handleSubmitWithEditedValues = () => {
    // Pass edited values to the submission process
    onSubmit(editedValues);
  };

  const displayGoToPreviousColumnButton = columnStartIndex > 0;
  const displayGoToNextColumnButton =
    columnStartIndex < dataColumns.length - LINKAGE_VISIBLE_COLUMNS;

  return (
    <>
      <ScrollableTableWrapper className="scrollable-wrapper">
        <TableContainer
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            position: 'relative',
          }}
          component={Paper}
        >
          {displayGoToPreviousColumnButton && (
            <TableColumnMoveButton
              disabled={!displayGoToPreviousColumnButton}
              onClick={() => setColumnStartIndex(columnStartIndex - 1)}
              $borderRadius="1rem 0 0 1rem"
              style={{
                position: 'sticky',
                top: 0,
                height: '400px', // Match ScrollableTableWrapper's max-height
                alignSelf: 'flex-start',
                zIndex: 1,
              }}
              data-arrow="left"
            >
              <RotatedArrowIcon rotate={90} />
            </TableColumnMoveButton>
          )}
          <StyledTable>
            <StyledTableHead>
              <StyledRow>
                {visibleColumns.map(column => {
                  const linkedColumn = linkedColumns.find(
                    lk => lk.importedColumnUuid === column.id
                  );
                  const isColumnLinked = !!linkedColumn;
                  const hasErrors = isColumnLinked && columnHasErrors(column.id);
                  return (
                    <StyledHeaderCell
                      key={column.id}
                      $isColumnLinked={isColumnLinked}
                      $hasErrors={hasErrors}
                      onClick={() => handleColumnHeaderClick(column.id)}
                    >
                      <TableCellContentWrapper>
                        <StyledHeaderCellText>{column.name}</StyledHeaderCellText>
                        <LinkingIconButton onClick={() => handleColumnHeaderClick(column.id)}>
                          {(() => {
                            if (isColumnLinked && !hasErrors) {
                              return <StyledCircleCheckIcon />;
                            }
                            if (isColumnLinked && hasErrors) {
                              return (
                                <Tooltip
                                  title={`Linked to ${linkedColumn.linkedTargetTitle}, but some values are invalid. Hover the highlighted cells for details.`}
                                  placement="top"
                                  arrow
                                >
                                  <WarningAmberIcon
                                    sx={{ color: '#ed6c02', fontSize: '2rem' }}
                                    aria-label="Linked with invalid values"
                                  />
                                </Tooltip>
                              );
                            }
                            return <LinkingIcon width="2rem" height="2rem" overflow="visible" />;
                          })()}
                        </LinkingIconButton>
                      </TableCellContentWrapper>
                    </StyledHeaderCell>
                  );
                })}
              </StyledRow>
            </StyledTableHead>
            <StyledTableBody>
              {visibleRows.map(row => (
                <StyledRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  {visibleColumns.map((column, index) => {
                    const isColumnLinked = linkedColumns.some(
                      lk => lk.importedColumnUuid === column.id
                    );
                    const originalValue = row.cells[columnStartIndex + index]?.value || '';
                    const cellValue = getCellValue(row.id, column.id, originalValue);
                    const validation = isColumnLinked
                      ? getCellValidation(column.id, cellValue)
                      : null;
                    const isValid = validation ? validation.isValid : null;
                    const isEditing =
                      editingCell?.rowId === row.id && editingCell?.columnId === column.id;

                    const cellContent = (
                      <StyledBodyCell
                        key={`${row.id}-${column.id}`}
                        $isColumnLinked={isColumnLinked}
                        $isValid={isValid}
                        $isEditing={isEditing}
                        onClick={() => !isEditing && handleCellClick(row.id, column.id)}
                      >
                        <CellContentWrapper>
                          {isEditing ? (
                            <CellInput
                              defaultValue={cellValue}
                              autoFocus
                              onBlur={e => handleCellSave(row.id, column.id, e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  handleCellSave(row.id, column.id, e.currentTarget.value);
                                } else if (e.key === 'Escape') {
                                  handleCellCancel();
                                }
                              }}
                              onClick={e => e.stopPropagation()}
                            />
                          ) : (
                            <span>{cellValue}</span>
                          )}
                          {!isEditing && isValid === false && (
                            <ErrorOutlineIcon
                              sx={{
                                color: '#d32f2f',
                                fontSize: '2rem',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {!isEditing && isValid && validation?.warningMessage && (
                            <WarningAmberIcon
                              sx={{
                                color: '#ed6c02',
                                fontSize: '2rem',
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </CellContentWrapper>
                      </StyledBodyCell>
                    );

                    // Wrap with tooltip if there's an error or warning
                    const tooltipMessage = validation?.isValid
                      ? validation.warningMessage
                      : validation?.errorMessage;
                    if (tooltipMessage) {
                      return (
                        <Tooltip
                          key={`${row.id}-${column.id}`}
                          title={tooltipMessage}
                          placement="top"
                          arrow
                        >
                          {cellContent}
                        </Tooltip>
                      );
                    }

                    return cellContent;
                  })}
                </StyledRow>
              ))}
            </StyledTableBody>
          </StyledTable>
          {displayGoToNextColumnButton && (
            <TableColumnMoveButton
              disabled={!displayGoToNextColumnButton}
              onClick={() => setColumnStartIndex(columnStartIndex + 1)}
              $borderRadius="0 1rem 1rem 0"
              style={{
                position: 'sticky',
                top: 0,
                height: '400px', // Match ScrollableTableWrapper's max-height
                alignSelf: 'flex-start',
                zIndex: 1,
              }}
            >
              <RotatedArrowIcon rotate={-90} />
            </TableColumnMoveButton>
          )}
        </TableContainer>
      </ScrollableTableWrapper>
      <LinkingTableFooter
        onSubmit={handleSubmitWithEditedValues}
        linkedColumns={linkedColumns}
        onSaveTemplate={onSaveTemplate}
        rowsLength={dataRows.length}
      />
    </>
  );
};

const StyledRow = styled(TableRow)`
  /* Keep default table-row display for proper alignment */
`;

const StyledTable = styled(Table)`
  flex: 1;
  /* Keep default table display for proper structure */
  width: 100%;
  table-layout: fixed; /* Ensure consistent column widths */
`;

const StyledTableHead = styled(TableHead)`
  /* Keep default table-header-group display */
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ theme }) => theme.palette.background.paper};
`;

const ScrollableTableWrapper = styled.div`
  display: flex;
  align-items: stretch;
  max-height: 400px;
  position: relative;
  overflow-y: auto; /* Revert to scrolling on wrapper */
  overflow-x: hidden;
  padding-left: 12px; /* Space for external scrollbar */
  margin-left: -12px; /* Compensate for padding */

  /* Custom scrollbar styling - positioned on the left outside */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => transparentize(0.9, theme.palette.brand.textPrimary)};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => transparentize(0.5, theme.palette.brand.textPrimary)};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => transparentize(0.3, theme.palette.brand.textPrimary)};
  }

  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => transparentize(0.5, theme.palette.brand.textPrimary)}
    ${({ theme }) => transparentize(0.9, theme.palette.brand.textPrimary)};

  /* Move scrollbar to left using direction */
  direction: rtl;

  /* Reset content direction */
  & > * {
    direction: ltr;
  }
`;

const StyledTableBody = styled(TableBody)`
  /* Keep default table-body display for proper structure */
`;

const StyledTableCell = styled(TableCell)<{ $isColumnLinked: boolean }>`
  padding: 1rem 2rem;
  width: 33.33%; /* Equal width for 3 columns */
  text-align: left;
  line-height: unset;
  color: ${({ theme }) => theme.palette.brand.textPrimary};

  ${({ $isColumnLinked, theme }) =>
    $isColumnLinked &&
    css`
      color: ${theme.palette.brand.white};
      background-color: ${theme.palette.brand.secondary};
    `}
`;

const StyledHeaderCell = styled(StyledTableCell)<{
  $isColumnLinked: boolean;
  $hasErrors?: boolean;
}>`
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.5rem;
  ${({ $isColumnLinked, $hasErrors, theme }) => {
    if ($isColumnLinked) {
      if ($hasErrors) {
        return css`
          background-color: #ffd9d9; /* Pink background for columns with errors */
          color: ${theme.palette.brand.textPrimary};
        `;
      }
      return css`
        background-color: #9dcc7a; /* Green background for columns without errors */
        color: ${theme.palette.brand.white};
      `;
    }
    return css`
      background-color: ${theme.palette.brand.backgroundDialog};
    `;
  }}
`;

const StyledBodyCell = styled(StyledTableCell)<{
  $isEditing?: boolean;
  $isValid?: boolean | null;
}>`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.5rem;
  cursor: ${({ $isEditing }) => ($isEditing ? 'auto' : 'pointer')};

  ${({ $isColumnLinked, $isValid, $isEditing, theme }) => {
    // Editing state styling
    if ($isEditing) {
      return css`
        background-color: ${theme.palette.brand.white};
        border: 2px solid ${theme.palette.brand.primary};
      `;
    }

    if ($isColumnLinked) {
      if ($isValid === false) {
        return css`
          background-color: #ffd9d9; /* Light red/pink for invalid */
          color: ${theme.palette.error.main};
        `;
      }
      if ($isValid === true) {
        return css`
          background-color: #9dcc7a; /* Light green for valid */
          color: ${theme.palette.success.dark};
        `;
      }
      return css`
        background-color: ${theme.palette.brand.primary};
        color: ${theme.palette.brand.white};
      `;
    }
    return css`
      background-color: ${theme.palette.brand.backgroundMain};
    `;
  }}
`;

const TableCellContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LinkingIconButton = styled(IconButton)`
  flex: 0 0 2rem;
`;
const TableColumnMoveButton = styled(IconButton)<{ $borderRadius: string }>`
  background-color: ${({ theme }) => theme.palette.brand.textSecondary};
  border-radius: ${({ $borderRadius }) => $borderRadius};
  display: flex;
  align-items: center;
  justify-content: center;
  path {
    stroke: ${({ theme }) => theme.palette.brand.white};
  }
  &.Mui-disabled {
    opacity: 0.3;
    background-color: ${({ theme }) => theme.palette.brand.textSecondary};
    path {
      stroke: ${({ theme }) => transparentize(0.5, theme.palette.brand.white)};
    }
  }
  :hover {
    background-color: ${({ theme }) => darken(0.1, theme.palette.brand.textSecondary)};
  }
`;
const RotatedArrowIcon = styled(ArrowDownIcon)<{ rotate: number }>`
  transform: rotate(${({ rotate }) => rotate}deg);
  width: 1.5rem;
  height: 1.5rem;
`;

const StyledHeaderCellText = styled.div`
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.5rem;
`;

const CellContentWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  /* Text takes available space, icon stays at end but not pushed to edge */
  & > span:first-child {
    flex: 1;
    margin-right: auto;
  }
`;

const StyledCircleCheckIcon = styled(CircleCheckIcon)`
  width: 2rem;
  height: 2rem;
  path,
  polygon {
    fill: ${({ theme }) => theme.palette.brand.white};
  }
`;

const CellInput = styled(Input)`
  width: 100%;
  font-size: 1.4rem;

  & input {
    padding: 0.5rem;
    border: none;
    outline: none;
    background: transparent;
    width: 100%;
  }

  &.MuiInput-root:before,
  &.MuiInput-root:after {
    display: none;
  }
`;

export { LinkingTable };
